/**
 * EM/Lead Portal — Assessment Domains Assignment Page
 * Engagement Managers/Leads assign individual assessments (grouped by category)
 * to their client organizations.
 * After assignment, assessments are immediately visible to SME and Client portals.
 *
 * Semantics (allowed_domains):
 *   null   → unrestricted (all assessments)
 *   []     → no access (disabled)
 *   [...]  → restricted to listed assessment domain_ids.
 *            Legacy entries may be category names (expanded to all their assessments).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Alert, Stack, CircularProgress, Card, CardContent,
  Divider, Autocomplete, TextField, Checkbox, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { CheckCircle, Block, Assignment, CheckBoxOutlineBlank, CheckBox, Close, RemoveCircle } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface ClientOrg {
  id: string;
  name: string;
  domain: string;
  allowed_domains: string[] | null;
  is_active: boolean;
}

interface SubDomain {
  name: string;
  domain_id: string;
  assessment_id: string;
}

interface CategoryDomain {
  name: string;
  sub_domains: SubDomain[];
}

interface Assessment extends SubDomain {
  category: string;
}

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const ClientDomains: React.FC = () => {
  const [orgs, setOrgs] = useState<ClientOrg[]>([]);
  const [categories, setCategories] = useState<CategoryDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  // Assignment form state
  const [selectedOrg, setSelectedOrg] = useState<ClientOrg | null>(null);
  const [selectedAssessments, setSelectedAssessments] = useState<Assessment[]>([]);
  // Disable all domains confirmation dialog
  const [disableDialog, setDisableDialog] = useState<ClientOrg | null>(null);
  const [disabling, setDisabling] = useState(false);

  // Flattened assessment list (grouped by category in the picker) and id→name map.
  const allAssessments = useMemo<Assessment[]>(
    () => categories.flatMap((c) => c.sub_domains.map((s) => ({ ...s, category: c.name }))),
    [categories],
  );
  const didToName = useMemo(() => {
    const m: Record<string, string> = {};
    categories.forEach((c) => c.sub_domains.forEach((s) => { m[s.domain_id] = s.name; }));
    return m;
  }, [categories]);
  // Stored entries are domain_ids (new) or category names (legacy); show a label for either.
  const labelForEntry = (entry: string) => didToName[entry] || entry;

  const fetchAssignmentData = useCallback(() => {

    setLoading(true);
    Promise.all([
      authFetch('/api/sme/client-organizations').then((r) => r.json()),
      authFetch('/api/admin/assessment-domains').then((r) => r.json()),
    ])
      .then(([orgData, domainData]) => {
        setOrgs(orgData.organizations || []);
        if (domainData.success) setCategories(domainData.categories || []);
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });

  }, []);

  useEffect(() => { fetchAssignmentData(); }, [fetchAssignmentData]);

  // When selecting a client, pre-fill their current domain assignments
  const handleOrgSelect = (org: ClientOrg | null) => {
    setSelectedOrg(org);
    setError('');
    setSuccess('');
    if (org) {
      const current = org.allowed_domains || [];
      // Pre-select by domain_id (new) or by category name (legacy = all its assessments).
      setSelectedAssessments(
        allAssessments.filter((a) => current.includes(a.domain_id) || current.includes(a.category)),
      );
    } else {
      setSelectedAssessments([]);
    }
  };

  /** Save domain assignments for the selected org */
  const handleAssign = async () => {
    if (!selectedOrg) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const domainIds = selectedAssessments.map((a) => a.domain_id);
      const res = await authFetch(`/api/sme/client-domains/${selectedOrg.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed_domains: domainIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      // Update the table
      setOrgs((prev) =>
        prev.map((o) =>
          o.id === selectedOrg.id ? { ...o, allowed_domains: domainIds } : o
        )
      );
      const label = domainIds.length === 0
        ? 'All assessments removed — client has no assessment access'
        : `${domainIds.length} assessment(s) assigned`;
      setSuccess(`${label} to ${selectedOrg.name}`);
      setSelectedOrg(null);
      setSelectedAssessments([]);
    } catch (err: any) {
      setError(err.message || 'Failed to assign domains.');
    } finally {
      setSaving(false);
    }
  };

  /** Remove a single domain from a client org (inline action) */
  const handleRemoveDomain = async (org: ClientOrg, domainToRemove: string) => {
    const updated = (org.allowed_domains || []).filter((d) => d !== domainToRemove);
    try {
      const res = await authFetch(`/api/sme/client-domains/${org.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed_domains: updated }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove domain');
      setOrgs((prev) =>
        prev.map((o) => (o.id === org.id ? { ...o, allowed_domains: updated } : o))
      );
      setSuccess(`Removed "${domainToRemove}" from ${org.name}${updated.length === 0 ? ' — client now has no assessment access' : ''}`);
    } catch (err: any) {
      setError(err.message || 'Failed to remove domain');
    }
  };

  /** Disable all assessment domains for a client (set to empty list) */
  const handleDisableAll = async () => {
    if (!disableDialog) return;
    setDisabling(true);
    try {
      const res = await authFetch(`/api/sme/client-domains/${disableDialog.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed_domains: [] }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to disable');
      setOrgs((prev) =>
        prev.map((o) => (o.id === disableDialog.id ? { ...o, allowed_domains: [] } : o))
      );
      setSuccess(`All assessment domains disabled for ${disableDialog.name}`);
      setDisableDialog(null);
    } catch (err: any) {
      setError(err.message || 'Failed to disable domains');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Assessment Domains Assignment
        </Typography>
        <RefreshButton onRefresh={fetchAssignmentData} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Assign individual assessments (grouped by category) to your client organizations.
        Clients and SMEs will see the assigned assessments immediately after saving.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Assignment Form Card */}
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            <Assignment sx={{ mr: 1, verticalAlign: 'middle', color: '#C6007E' }} />
            Assign Domains to Client
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            {/* Client Dropdown */}
            <Autocomplete
              options={orgs}
              value={selectedOrg}
              onChange={(_, val) => handleOrgSelect(val)}
              getOptionLabel={(o) => `${o.name} (${o.domain})`}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.domain} — {o.allowed_domains === null
                        ? 'Unrestricted (all domains)'
                        : (o.allowed_domains || []).length === 0
                        ? 'No access (disabled)'
                        : `${o.allowed_domains.length} domain(s) assigned`}
                    </Typography>
                  </Box>
                </li>
              )}
              sx={{ minWidth: 320, flex: 1 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Client Organization" placeholder="Search by name..." />
              )}
            />

            {/* Assessment multi-select (grouped by category) */}
            <Autocomplete
              multiple
              options={allAssessments}
              value={selectedAssessments}
              onChange={(_, val) => setSelectedAssessments(val)}
              disableCloseOnSelect
              groupBy={(o) => o.category}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.domain_id === v.domain_id}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.domain_id}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <Typography variant="body2">{option.name}</Typography>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.domain_id}
                    label={option.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))
              }
              sx={{ minWidth: 380, flex: 1.5 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Assessments"
                  placeholder={selectedAssessments.length === 0 ? 'No assessments = no access...' : ''}
                />
              )}
            />

            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={handleAssign}
              disabled={!selectedOrg || saving}
              sx={{
                height: 56,
                px: 4,
                backgroundColor: '#C6007E',
                '&:hover': { backgroundColor: '#A30066' },
              }}
            >
              {saving ? 'Assigning...' : 'Assign Domains'}
            </Button>
          </Stack>

          {selectedOrg && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Current assignment for <strong>{selectedOrg.name}</strong>:{' '}
                {selectedOrg.allowed_domains === null ? (
                  <Chip label="All domains (unrestricted)" size="small" color="info" variant="outlined" sx={{ ml: 0.5 }} />
                ) : (selectedOrg.allowed_domains || []).length === 0 ? (
                  <Chip label="No access (disabled)" size="small" color="error" variant="outlined" sx={{ ml: 0.5 }} />
                ) : (
                  selectedOrg.allowed_domains.map((d) => (
                    <Chip key={d} label={labelForEntry(d)} size="small" color="primary" variant="outlined" sx={{ ml: 0.5, mb: 0.5 }} />
                  ))
                )}
              </Typography>
              {selectedAssessments.length === 0 && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                  ⚠ No assessments selected — saving will disable the client's assessment access entirely.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ mb: 3 }} />

      {/* Current Assignments Table */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Current Client Assignments
      </Typography>
      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Assigned Assessments</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  No assigned client organizations found. Contact an Admin to assign you to client engagements.
                </TableCell>
              </TableRow>
            ) : (
              orgs.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell
                    sx={{ fontWeight: 500, cursor: 'pointer' }}
                    onClick={() => handleOrgSelect(org)}
                  >
                    {org.name}
                  </TableCell>
                  <TableCell>{org.domain || '—'}</TableCell>
                  <TableCell>
                    {org.allowed_domains === null ? (
                      <Chip label="All domains (unrestricted)" size="small" color="info" variant="outlined" />
                    ) : (org.allowed_domains || []).length === 0 ? (
                      <Chip label="No access (disabled)" size="small" color="error" variant="outlined" />
                    ) : (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {org.allowed_domains.map((d) => (
                          <Chip
                            key={d}
                            label={labelForEntry(d)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            onDelete={() => handleRemoveDomain(org, d)}
                            deleteIcon={
                              <Tooltip title={`Remove "${labelForEntry(d)}"`}>
                                <Close fontSize="small" />
                              </Tooltip>
                            }
                          />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    {org.is_active ? (
                      <Chip icon={<CheckCircle />} label="Active" size="small" color="success" />
                    ) : (
                      <Chip icon={<Block />} label="Disabled" size="small" color="error" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Edit domains">
                        <IconButton size="small" color="primary" onClick={() => handleOrgSelect(org)}>
                          <Assignment fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {org.allowed_domains !== null && (org.allowed_domains || []).length > 0 && (
                        <Tooltip title="Disable all domains">
                          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDisableDialog(org); }}>
                            <RemoveCircle fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Disable All Domains Confirmation Dialog */}
      <Dialog open={!!disableDialog} onClose={() => setDisableDialog(null)}>
        <DialogTitle>Disable All Assessment Domains?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove all assessment domain access for <strong>{disableDialog?.name}</strong>.
            The client will not be able to run any assessments or submit assessment requests until domains are re-assigned.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDisableAll} disabled={disabling}>
            {disabling ? 'Disabling...' : 'Disable All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDomains;
